import { startOfToday } from "date-fns";
import { all, call, put, select, takeLatest } from "redux-saga/effects";

import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import { Route } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import routeSettings from "@app/scene/routes/utils/routeSettings";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";
import routeFieldModel, { RouteFieldName } from "@app/util/fieldModel/RouteFieldModel";
import { createEmptyFilterModel, extractSimpleConditionForField } from "@app/util/filters/utils";
import { isSimpleCondition } from "@app/util/viewModel/assert";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import {
  applyUpcomingViewStateSettings,
  fetchUpcomingRoutes,
  initializeUpcomingView,
} from "./actions";
import { defaultViewState, PAGE_SIZE } from "./reducer";
import { getUpcomingViewState } from "./selectors";

export function* onInitializeUpcomingView() {
  try {
    const persistedViewState: ViewState = routeSettings.getUpcomingViewSettings(
      routeFieldModel,
      defaultViewState
    );
    yield put(applyUpcomingViewStateSettings(persistedViewState));
    yield put(fetchUpcomingRoutes.request({ viewState: {} }));
    yield put(initializeUpcomingView.success());
  } catch (error) {
    yield put(initializeUpcomingView.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchUpcomingRoutes({
  payload,
}: ReturnType<typeof fetchUpcomingRoutes.request>) {
  try {
    if (!payload.fetchOnlyWithoutFilters) {
      yield put(applyUpcomingViewStateSettings(payload.viewState));
    }

    const viewState: ViewState = yield select(getUpcomingViewState);

    if (!payload.fetchOnlyWithoutFilters) {
      routeSettings.setUpcomingViewSettings(viewState);
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      payload.fetchOnlyWithoutFilters && payload.viewState.range
        ? payload.viewState.range.startRow
        : viewState.range.startRow;
    const $limit =
      payload.fetchOnlyWithoutFilters && payload.viewState.range
        ? payload.viewState.range.endRow - payload.viewState.range.startRow
        : viewState.range.endRow - viewState.range.startRow;

    const orgId: Organization["id"] = yield select(getOrganizationId);

    const [quickFilter, filter] = extractSimpleConditionForField(
      viewState.filter,
      RouteFieldName.QUICK_FILTER
    );

    const $filters: Record<string, unknown> = {
      ...convertToPlatformFilterModel(
        payload.fetchOnlyWithoutFilters ? createEmptyFilterModel() : filter,
        viewState.columns,
        routeFieldModel
      ),
      includeAccessStatus: true,
      includePolyline: true,
      "routeDetail.startAt": {
        [PlatformFilterOperator.GREATER_THAN_OR_EQUAL]: startOfToday().toISOString(),
      },
    };

    if (!payload.fetchOnlyWithoutFilters && quickFilter && isSimpleCondition(quickFilter)) {
      const { value } = quickFilter;
      if (!("$and" in $filters)) {
        $filters.$and = [];
      }
      ($filters.$and as unknown[]).push({ $or: [{ name: { $in: value } }] });
    }

    const response: ListResponse<Route> = yield callApi("fetchUnifiedRoutes", orgId, {
      $filters,
      $limit,
      $offset,
      $order: convertToPlatformSortModel(viewState.sort),
    });

    // The persisted page may no longer exist (upcoming routes shrink over time).
    // Force the user back to the first page instead of showing an empty page.
    if (
      !payload.fetchOnlyWithoutFilters &&
      viewState.range.startRow > 0 &&
      viewState.range.startRow >= response.total
    ) {
      yield put(applyUpcomingViewStateSettings({ range: { endRow: PAGE_SIZE, startRow: 0 } }));
      // Hand the caller's callbacks to the replacement fetch — this one returns
      // without settling them, so dropping them would leave the caller waiting.
      yield put(
        fetchUpcomingRoutes.request({
          dataCallback: payload.dataCallback,
          failCallback: payload.failCallback,
          viewState: {},
        })
      );
      return;
    }

    if (payload.dataCallback) {
      yield call(payload.dataCallback, response);
    }

    yield put(
      fetchUpcomingRoutes.success({
        routes: response.data,
        totalFilteredRecords: response.total,
        totalRecords: response.accessible ?? response.total,
      })
    );
  } catch (error) {
    if (payload.failCallback) {
      yield call(payload.failCallback);
    }
    yield put(fetchUpcomingRoutes.failure(error));
    yield put(handleError({ error }));
  }
}

export function* upcomingViewSaga() {
  yield all([
    takeLatest(initializeUpcomingView.request, onInitializeUpcomingView),
    takeLatest(fetchUpcomingRoutes.request, onFetchUpcomingRoutes),
  ]);
}
